/**
 * BILLENTYŰZET-ELSŐ AZ ELSŐ NAPTÓL (D9) — a váz, nem a teljes billentyűkiosztás.
 *
 * Miért már most, amikor még nincs munkalista: a 2.6/16 bukásmódja. A
 * fókuszkezelés NEM CSS-kérdés, hanem komponensszerkezet — ha utólag húzzuk
 * be, MINDEN KÉPERNYŐT ÚJRA KELL NYITNI. Ugyanaz az érv, amiért az i18n is
 * az 1. lépésben van.
 *
 * Amit a váz megold, és amit egy későbbi képernyő már készen kap:
 *   • egy REGISZTER, amibe a képernyők bejelentkeznek — nem szórt
 *     `window.addEventListener` hívások, amiket senki nem lát együtt
 *   • egy hely, ahol eldől, mikor NEM nyel a rövidítés: beviteli mezőben
 *     gépelve az `M` betű az `M` betű, nem a „megvan" parancs
 *   • a súgó, ami a regiszterből épül, tehát nem tud elavulni
 *
 * A billentyűk jelentése a szójegyzékben van rögzítve — EGY BILLENTYŰ EGY
 * DOLGOT JELENT, végig a felületen.
 */

import { useEffect } from 'react';

export interface Shortcut {
  /** A billentyű, ahogy a `KeyboardEvent.key` adja: 'Enter', 'm', '/', '?' */
  key: string;
  /**
   * KELL-E HOZZÁ `Ctrl` (vagy macOS-en `Cmd`)?
   *
   * Nem díszítés: enélkül a `Ctrl+K` bejegyzése a PUSZTA `k`-ra is elsülne,
   * és a paletta minden „k" leütésre kinyílna — a „Kovács" beírása
   * használhatatlan lenne. A D9 kiosztásában három ilyen van: `Ctrl+K` a
   * paletta, `Ctrl+1`/`Ctrl+2` a felületek.
   */
  ctrl?: boolean;
  /**
   * KELL-E HOZZÁ `Shift`? Csak NEVES billentyűn (`Enter`, `ArrowDown`) van
   * értelme — lásd a `findMatch` levezetését.
   *
   * A `Shift+Enter` az `Enter` SZŰKEBB alakja a hiánylistán: „bekérés, de
   * csak ezen a soron" (#49). Ugyanaz az ige, kisebb hatókörrel — a
   * szójegyzék „egy billentyű egy dolgot jelent" szabálya ezért áll.
   */
  shift?: boolean;
  /** i18n-kulcs a súgóhoz — SOHA nem beégetett szöveg. */
  labelKey: string;
  run: () => void;
  /** Beviteli mezőben is fusson? Alapból nem. Az `Escape` a kivétel. */
  allowInInput?: boolean;
}

/**
 * A BILLENTYŰ OLVASHATÓ ALAKJA — a súgónak és a React-kulcsnak.
 *
 * A kettő ugyanaz a probléma: a `Ctrl+K` és a puszta `k` KÉT rövidítés, és
 * a nyers `shortcut.key` mindkettőre `'k'`-t adna. A súgóban azonos
 * sorokként állnának, a listában pedig ütköző kulcsként.
 */
export function shortcutLabel(shortcut: Shortcut): string {
  const key = shortcut.ctrl ? shortcut.key.toUpperCase() : shortcut.key;
  const prefix = `${shortcut.ctrl ? 'Ctrl+' : ''}${shortcut.shift ? 'Shift+' : ''}`;
  return `${prefix}${key}`;
}

/**
 * AMI SZÖVEGET FOGAD — és csak az.
 *
 * A jelölő vezérlők (`radio`, `checkbox`) és a gomb-alakú `input`-ok NEM
 * gépelési célpontok: rajtuk a leütés nem betű, hanem parancs. Ha az egész
 * `INPUT` tag ide tartozna, a sor-mód `Enter`-e ELNÉMULNA pont a leggyakoribb
 * úton — a könyvelő kiválasztja a hiánytételt (a fókusz a rádiógombra kerül),
 * megnyomja az `Enter`-t, és nem történik semmi. *(A sor-mód tesztjei fogták
 * meg, 09-03.)*
 *
 * A TÍPUSOK FEHÉRLISTA HELYETT FEKETELISTA: az `input` alapértelmezése
 * `text`, és a jövőbeli szöveges típusok (`email`, `search`, `url`, dátumok)
 * mind gépelősek. Egy fehérlistát minden új típusnál bővíteni kellene, és a
 * felejtés ára CSENDES ADATVESZTÉS — a felület a nevet keresve hajtana végre
 * parancsot.
 */
const COMMAND_INPUT_TYPES = new Set([
  'radio',
  'checkbox',
  'button',
  'submit',
  'reset',
  'image',
  'file',
  'range',
  'color',
]);

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target instanceof HTMLInputElement) {
    return !COMMAND_INPUT_TYPES.has(target.type);
  }
  const tag = target.tagName;
  return tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

/**
 * AMIT A BÖNGÉSZŐ MAGA AKTIVÁL — és amit ezért nem vehetünk el.
 *
 * Egy fókuszált gombon az `Enter` és a `Space` A GOMBÉ. A `useShortcuts`
 * viszont a `run()` ELŐTT hív `preventDefault()`-ot, tehát regisztrált
 * `Enter`-rövidítés mellett a gomb kattintása elmarad, és helyette a
 * rövidítés fut le.
 *
 * MIÉRT EZ A LEGROSSZABB HELYEN FÁJ. A sor-módban az `Enter` akkor van
 * bejegyezve, amikor van kijelölt hiánytétel — és a bukott mentések sávjának
 * gombjai („Elvetem", „Újra mentem", „Ugrás az elsőre") pont ilyen
 * állapotban kellenek. Az a kijárat, amin a 2.6/22 harmadik pontja áll,
 * billentyűzetről elérhetetlen lenne. *(Code review fogta meg, 09-03.)*
 *
 * A `Tab` és a nyilak NEM tartoznak ide: azokat a gomb nem aktiválja, tehát
 * a rövidítés elveheti őket.
 */
const ACTIVATION_KEYS = new Set(['enter', ' ', 'spacebar']);

function isActivationTarget(target: EventTarget | null, key: string): boolean {
  if (!ACTIVATION_KEYS.has(key.toLowerCase())) {
    return false;
  }
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target instanceof HTMLButtonElement || target instanceof HTMLAnchorElement) {
    return true;
  }
  return target.tagName === 'SUMMARY' || target.getAttribute('role') === 'button';
}

/**
 * A MÓDOSÍTÓS KOMBINÁCIÓK A BÖNGÉSZŐÉ — hacsak nincs rájuk BEJEGYZETT
 * rövidítés.
 *
 * Korábban itt egyetlen beégetett kivétel állt (`Ctrl+K`). Az addig volt jó,
 * amíg egy ilyen kombináció volt: a `Ctrl+1`/`Ctrl+2` felvételéhez már ezt a
 * függvényt is bővíteni kellett volna, és a kettő elcsúszása CSENDES —
 * a rövidítés bejegyezve áll, a súgó kiírja, és mégsem sül el.
 *
 * Mostantól a REGISZTER dönt: ami be van jegyezve módosítósként, azt
 * elvesszük; a többi kombináció (`Ctrl+T`, `Ctrl+W`, `Alt+←`) a böngészőé.
 */
function isBrowserCombo(shortcuts: Shortcut[], event: KeyboardEvent): boolean {
  const modified = event.ctrlKey || event.metaKey || event.altKey;
  if (!modified) {
    return false;
  }

  // Az `Alt` SOHA nem a miénk: a D9 kiosztásában nincs `Alt`-os billentyű, és
  // az operációs rendszerek több helyen lefoglalják.
  if (event.altKey) {
    return true;
  }

  return !shortcuts.some(
    (shortcut) => shortcut.ctrl === true && shortcut.key.toLowerCase() === event.key.toLowerCase()
  );
}

/** Van-e ugyanerre a billentyűre `Shift`-es testvére a regiszterben? */
function hasShiftVariant(shortcuts: Shortcut[], shortcut: Shortcut): boolean {
  return shortcuts.some(
    (other) =>
      other.shift === true &&
      other.ctrl === shortcut.ctrl &&
      other.key.toLowerCase() === shortcut.key.toLowerCase()
  );
}

function findMatch(
  shortcuts: Shortcut[],
  event: KeyboardEvent,
  typing: boolean
): Shortcut | undefined {
  const pressedCtrl = event.ctrlKey || event.metaKey;

  return shortcuts.find((shortcut) => {
    if (shortcut.key.toLowerCase() !== event.key.toLowerCase()) {
      return false;
    }
    // A MÓDOSÍTÓNAK EGYEZNIE KELL, MINDKÉT IRÁNYBAN. A `Ctrl+K` ne süljön el
    // puszta `k`-ra, és a puszta `m` se `Ctrl+M`-re — az utóbbi a böngészőé.
    if ((shortcut.ctrl === true) !== pressedCtrl) {
      return false;
    }
    /**
     * A `Shift` CSAK OTT SZÁMÍT, AHOL VAN MIT MEGKÜLÖNBÖZTETNI.
     *
     * A `shift: true` bejegyzés MINDIG kéri a `Shift`-et. A `Shift` NÉLKÜLI
     * bejegyzést viszont csak akkor zárja ki egy lenyomott `Shift`, ha
     * ugyanarra a billentyűre VAN shiftes testvére a regiszterben — így a
     * `Shift+Enter` a soronkénti bekérést sütí el az egész cég helyett.
     *
     * ⚠️ MIÉRT NEM EGYSZERŰEN „A `Shift` NE LEGYEN LENYOMVA". Két helyen is
     * elvenne olyat, ami ma működik, és MINDKÉT bukás néma:
     *
     *   • az `Escape` a KIJÁRAT (`App.tsx`: teljes képernyő és súgó, a
     *     beállítások lapja) — egy lenyomva felejtett `Shift` mellett nem
     *     lehetne kilépni, miközben a felület kiírja, hogy „Kilépés: Esc";
     *   • a `?` a legtöbb kiosztáson MAGA IS `Shift`-tel készül
     *     (`Shift+,` magyarul), tehát a súgó válna elérhetetlenné.
     *
     * *(Code review fogta meg, méréssel, 09-18.)*
     */
    if (shortcut.shift === true) {
      if (!event.shiftKey) {
        return false;
      }
    } else if (event.shiftKey && hasShiftVariant(shortcuts, shortcut)) {
      return false;
    }
    // Gépelés közben a betű BETŰ MARAD. Enélkül a felület a nevet keresve
    // „megvan"-t hajtana végre — csendes adatvesztés. A MÓDOSÍTÓS
    // rövidítés viszont mezőben is él: a `Ctrl+K` nem betű.
    return shortcut.allowInInput || shortcut.ctrl === true || !typing;
  });
}

export function useShortcuts(shortcuts: Shortcut[], enabled = true): void {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    function onKeyDown(event: KeyboardEvent) {
      if (isBrowserCombo(shortcuts, event)) {
        return;
      }

      // A GOMB SAJÁT AKTIVÁLÁSA ELŐBBRE VALÓ, mint bármelyik rövidítés.
      if (isActivationTarget(event.target, event.key)) {
        return;
      }

      const match = findMatch(shortcuts, event, isTypingTarget(event.target));
      if (match) {
        event.preventDefault();
        match.run();
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [shortcuts, enabled]);
}
